import { ReactNode, useEffect, useState } from 'react'
import { InputGroup, Radio, RadioButton, Select, toastManager } from '@systemeio/components'
import { ArrowDownIcon } from '@systemeio/icons'
import { useTranslation } from 'next-i18next/pages'
import { AccessDenied } from 'shared/errors/access-denied'
import { BadRequest } from 'shared/errors/bad-request'
import { UnauthorizedResponse } from 'shared/errors/unauthorized-response'
import useUser, { isAuthorizedUser } from 'shared/hooks/use-user'
import { getCountryData } from 'shared/utils/get-country-data'
import { useUserSettings } from '../../api/use-user-settings'
import { ProfileLayout } from '../../components/profile-layout'
import { useUpdateUserSettings } from '../api/use-update-user-settings'
import { CustomerTypeEnum } from '../enums/customer-type-enum'
import {
  EditableUserSettings,
  UserSettingsErrorResponseContentInterface,
  UserSettingsErrorsInterface,
} from '../types/user-settings-interface'
import UserDomainAccountFields from './user-domain-account-fields'
import UserSettingsSkeleton from './user-settings-skeleton'

const defaultErrors: UserSettingsErrorsInterface = {
  taxNumber: '',
  address: '',
  city: '',
  companyName: '',
  country: '',
  customerType: '',
  firstName: '',
  phone: '',
  state: '',
  lastName: '',
  postCode: '',
  companyNumber: '',
  displayName: '',
  profileImage: '',
  bio: '',
}

const defaultValues: EditableUserSettings = {
  postCode: null,
  state: null,
  phone: null,
  lastName: null,
  firstName: null,
  customerType: CustomerTypeEnum.Personal,
  city: null,
  companyName: null,
  companyNumber: null,
  address: null,
  country: null,
  taxNumber: null,
  displayName: null,
  profileImage: null,
  bio: null,
}

// TODO: add some validators and tanstack form https://app.shortcut.com/systeme/story/102625/introduce-tanstack-form
function UserSettings(): ReactNode {
  const { t } = useTranslation()
  const { user, isOwner, mutate: mutateUserData } = useUser()
  const { data: userSettings, error, mutate: mutateUserSettings } = useUserSettings()
  const [tempState, setTempState] = useState<EditableUserSettings>(defaultValues)
  const [errors, setErrors] = useState<UserSettingsErrorsInterface>(defaultErrors)

  const { mutate: updateUserSettings, isPending } = useUpdateUserSettings({
    onSuccess: async response => {
      await mutateUserSettings(response, false)
      await mutateUserData(
        data =>
          data && isAuthorizedUser(data)
            ? {
                ...data,
                avatarUrl: response.userDomainAccount.profileImage?.path || data.avatarUrl,
              }
            : data,
        false,
      )
      toastManager.add({ type: 'success', title: t('global.changes_saved') })
    },
    onError: error => {
      if (error instanceof BadRequest) {
        const userSettingErrors =
          error.errors as unknown as UserSettingsErrorResponseContentInterface

        setErrors({
          firstName: userSettingErrors.fields?.user?.firstName?.join(''),
          lastName: userSettingErrors.fields?.user?.lastName?.join(''),
          phone: userSettingErrors.fields?.user?.phone?.join(''),
          address: userSettingErrors.fields?.user?.address?.join(''),
          city: userSettingErrors.fields?.user?.city?.join(''),
          country: userSettingErrors.fields?.user?.country?.join(''),
          postCode: userSettingErrors.fields?.user?.postCode?.join(''),
          state: userSettingErrors.fields?.user?.state?.join(''),
          taxNumber: userSettingErrors.fields?.user?.taxNumber?.join(''),
          companyName: userSettingErrors.fields?.user?.companyName?.join(''),
          companyNumber: userSettingErrors.fields?.user?.companyNumber?.join(''),
          customerType: userSettingErrors.fields?.user?.customerType?.join(''),
          displayName: userSettingErrors.fields?.userDomainAccount?.displayName?.join(''),
          bio: userSettingErrors.fields.userDomainAccount?.bio?.join(''),
        })
      }
    },
  })

  useEffect(() => {
    if (error instanceof UnauthorizedResponse) {
      globalThis.location.replace(error.redirect)
      return
    } else if (error instanceof AccessDenied) {
      globalThis.location.replace('/403')
    }
  }, [error])

  useEffect(() => {
    if (userSettings?.user) {
      setTempState({
        ...userSettings.user,
        companyNumber: userSettings.companyNumber,
        displayName: userSettings.userDomainAccount.displayName,
        profileImage: userSettings.userDomainAccount.profileImage,
        bio: userSettings.userDomainAccount.bio,
      })
    }
  }, [userSettings])

  const customerTypeOptions: { value: CustomerTypeEnum; label: string }[] = [
    {
      label: t('user.profile_settings.customer_type.options.personal.label'),
      value: CustomerTypeEnum.Personal,
    },
    {
      label: t('user.profile_settings.customer_type.options.company.label'),
      value: CustomerTypeEnum.Company,
    },
  ]

  return (
    <ProfileLayout
      title={t('profile.user_settings.profile.title')}
      isFetching={isPending}
      onSave={() =>
        updateUserSettings({
          companyNumber: tempState.companyNumber,
          user: {
            address: tempState.address,
            city: tempState.city,
            companyName: tempState.companyName,
            country: tempState.country,
            firstName: tempState.firstName,
            lastName: tempState.lastName,
            state: tempState.state,
            phone: tempState.phone,
            customerType: tempState.customerType,
            postCode: tempState.postCode,
            taxNumber: tempState.taxNumber,
          },
          userDomainAccount: {
            displayName: tempState.displayName,
            profileImage: tempState.profileImage?.id || null,
            bio: tempState.bio,
          },
        })
      }
    >
      {userSettings && isAuthorizedUser(user) ? (
        <div className="flex h-fit w-full flex-col gap-2 overflow-x-hidden rounded-md bg-bg-primary md:gap-5">
          <div className="bg-bg-accent/10 p-5">
            <span className="text-base font-bold text-text-primary">{`${t('global.email_address')}: ${
              userSettings.user.email
            }`}</span>
          </div>
          <div className="flex flex-col gap-7 px-5 pt-5 pb-7 md:gap-10 md:pb-10">
            <UserDomainAccountFields
              setProfileImage={profileImage => setTempState(prev => ({ ...prev, profileImage }))}
              setDisplayName={displayName => setTempState(prev => ({ ...prev, displayName }))}
              setDisplayBio={bio => setTempState(prev => ({ ...prev, bio }))}
              email={userSettings.user.email}
              errors={errors}
              displayName={tempState.displayName}
              bio={tempState.bio}
              profileImage={tempState.profileImage?.path || user.avatarUrl}
            />
            {!isOwner && (
              <>
                <div className="flex flex-col justify-between gap-7 *:flex-1 md:flex-row md:gap-10">
                  <div className="flex flex-col gap-1">
                    <label className="flex gap-0.5 text-sm font-medium text-text-primary">
                      {t('user.profile_settings.first_name.label')}
                      <span className="text-text-error">*</span>
                    </label>
                    <InputGroup.Root>
                      <InputGroup.Input
                        placeholder="John"
                        value={tempState.firstName || ''}
                        onChange={e =>
                          setTempState(prev => ({
                            ...prev,
                            firstName: e.target.value,
                          }))
                        }
                      />
                    </InputGroup.Root>
                    {errors.firstName && (
                      <p className="text-sm text-text-error">{errors.firstName}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="flex gap-0.5 text-sm font-medium text-text-primary">
                      {t('user.profile_settings.last_name')}
                      <span className="text-text-error">*</span>
                    </label>
                    <InputGroup.Root>
                      <InputGroup.Input
                        placeholder="Doe"
                        value={tempState.lastName || ''}
                        onChange={e =>
                          setTempState(prev => ({
                            ...prev,
                            lastName: e.target.value,
                          }))
                        }
                      />
                    </InputGroup.Root>
                    {errors.lastName && (
                      <p className="text-sm text-text-error">{errors.lastName}</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col justify-between gap-7 *:flex-1 md:flex-row md:gap-10">
                  <div className="flex flex-col gap-1">
                    <label className="flex gap-0.5 text-sm font-medium text-text-primary">
                      {t('user.profile_settings.phone_number')}
                      <span className="text-text-error">*</span>
                    </label>
                    <InputGroup.Root>
                      <InputGroup.Input
                        placeholder="1234567890"
                        type="number"
                        value={tempState.phone || ''}
                        onChange={e =>
                          setTempState(prev => ({
                            ...prev,
                            phone: e.target.value,
                          }))
                        }
                      />
                    </InputGroup.Root>
                    {errors.phone && <p className="text-sm text-text-error">{errors.phone}</p>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="flex gap-0.5 text-sm font-medium text-text-primary">
                      {t('user.profile_settings.street_address')}
                      <span className="text-text-error">*</span>
                    </label>
                    <InputGroup.Root>
                      <InputGroup.Input
                        placeholder={t('user.profile_settings.street_address')}
                        value={tempState.address || ''}
                        onChange={e =>
                          setTempState(prev => ({
                            ...prev,
                            address: e.target.value,
                          }))
                        }
                      />
                    </InputGroup.Root>
                    {errors.address && <p className="text-sm text-text-error">{errors.address}</p>}
                  </div>
                </div>
                <div className="flex flex-col justify-between gap-7 *:flex-1 md:flex-row md:gap-10">
                  <div className="flex flex-col gap-1">
                    <label className="flex gap-0.5 text-sm font-medium text-text-primary">
                      {t('user.profile_settings.city')}
                      <span className="text-text-error">*</span>
                    </label>
                    <InputGroup.Root>
                      <InputGroup.Input
                        placeholder="San Francisco"
                        value={tempState.city || ''}
                        onChange={e =>
                          setTempState(prev => ({
                            ...prev,
                            city: e.target.value,
                          }))
                        }
                      />
                    </InputGroup.Root>
                    {errors.city && <p className="text-sm text-text-error">{errors.city}</p>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="flex gap-0.5 text-sm font-medium text-text-primary">
                      {t('user.profile_settings.postal_code')}
                      <span className="text-text-error">*</span>
                    </label>
                    <InputGroup.Root>
                      <InputGroup.Input
                        placeholder="12345"
                        value={tempState.postCode || ''}
                        onChange={e =>
                          setTempState(prev => ({
                            ...prev,
                            postCode: e.target.value,
                          }))
                        }
                      />
                    </InputGroup.Root>
                    {errors.postCode && (
                      <p className="text-sm text-text-error">{errors.postCode}</p>
                    )}
                  </div>
                </div>
                <div className="flex flex-col justify-between gap-7 *:flex-1 md:flex-row md:gap-10">
                  <div className="flex flex-col gap-1">
                    <Select.Root
                      items={getCountryData(user?.dashboardLocale).map(option => ({
                        value: option.id,
                        label: option.caption,
                      }))}
                      value={tempState.country || null}
                      onValueChange={value =>
                        setTempState(prev => ({
                          ...prev,
                          country: value ?? null,
                        }))
                      }
                    >
                      <Select.Label required>{t('user.profile_settings.country')}</Select.Label>
                      <Select.Trigger
                        data-testid="user-settings-country-select"
                        data-invalid={!!errors.country || undefined}
                      >
                        <Select.Value placeholder={t('user.profile_settings.select_country')} />
                        <Select.Icon>
                          <ArrowDownIcon />
                        </Select.Icon>
                      </Select.Trigger>
                      <Select.Content>
                        <Select.List>
                          {getCountryData(user?.dashboardLocale).map(option => (
                            <Select.Item key={option.id} value={option.id}>
                              <Select.ItemText>{option.caption}</Select.ItemText>
                              <Select.ItemIndicator />
                            </Select.Item>
                          ))}
                        </Select.List>
                      </Select.Content>
                    </Select.Root>
                    {errors.country && <p className="text-sm text-text-error">{errors.country}</p>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-text-primary">
                      {t('user.profile_settings.state_province')}
                    </label>
                    <InputGroup.Root>
                      <InputGroup.Input
                        placeholder="California"
                        value={tempState.state || ''}
                        onChange={e =>
                          setTempState(prev => ({
                            ...prev,
                            state: e.target.value,
                          }))
                        }
                      />
                    </InputGroup.Root>
                    {errors.state && <p className="text-sm text-text-error">{errors.state}</p>}
                  </div>
                </div>
                <div className="flex flex-col justify-between gap-7 *:flex-1 md:flex-row md:gap-10">
                  <div className="flex flex-col gap-1">
                    <label className="text-sm font-medium text-text-primary">
                      {t('user.profile_settings.customer_type.label')}
                    </label>
                    <Radio.Group
                      className="flex gap-2"
                      value={tempState.customerType}
                      onValueChange={value =>
                        setTempState(prev => ({
                          ...prev,
                          customerType: value,
                        }))
                      }
                    >
                      {customerTypeOptions.map(o => (
                        <RadioButton key={String(o.value)} value={o.value} label={o.label} />
                      ))}
                    </Radio.Group>
                    {errors.customerType && (
                      <p className="mt-2 text-sm text-text-error">{errors.customerType}</p>
                    )}
                  </div>
                </div>
                {tempState.customerType === 'company' && (
                  <div className="flex flex-col gap-7 md:gap-10">
                    <div className="flex flex-col justify-between gap-7 *:flex-1 md:flex-row md:gap-10">
                      <div className="flex flex-col gap-1">
                        <label className="flex gap-0.5 text-sm font-medium text-text-primary">
                          {t('user.profile_settings.company_name')}
                          <span className="text-text-error">*</span>
                        </label>
                        <InputGroup.Root>
                          <InputGroup.Input
                            placeholder="Amazon"
                            value={tempState.companyName || ''}
                            onChange={e =>
                              setTempState(prev => ({
                                ...prev,
                                companyName: e.target.value,
                              }))
                            }
                          />
                        </InputGroup.Root>
                        {errors.companyName && (
                          <p className="text-sm text-text-error">{errors.companyName}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col justify-between gap-7 *:flex-1 md:flex-row md:gap-10">
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-text-primary">
                          {t('user.profile_settings.company_number')}
                        </label>
                        <InputGroup.Root>
                          <InputGroup.Input
                            placeholder="1234567890"
                            type="number"
                            value={tempState.companyNumber || ''}
                            onChange={e =>
                              setTempState(prev => ({
                                ...prev,
                                companyNumber: e.target.value,
                              }))
                            }
                          />
                        </InputGroup.Root>
                        {errors.companyNumber && (
                          <p className="text-sm text-text-error">{errors.companyNumber}</p>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-sm font-medium text-text-primary">
                          {t('user.profile_settings.tax_number')}
                        </label>
                        <InputGroup.Root>
                          <InputGroup.Input
                            placeholder="1234"
                            type="number"
                            value={tempState.taxNumber || ''}
                            onChange={e =>
                              setTempState(prev => ({
                                ...prev,
                                taxNumber: e.target.value,
                              }))
                            }
                          />
                        </InputGroup.Root>
                        {errors.taxNumber && (
                          <p className="text-sm text-text-error">{errors.taxNumber}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <UserSettingsSkeleton />
      )}
    </ProfileLayout>
  )
}

export default UserSettings
